import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Users, 
    UserPlus, 
    UserMinus,
    Edit3, 
    Search, 
    Filter,
    Crown,
    Star,
    CheckCircle,
    XCircle,
    Calendar,
    Mail,
    User,
    Shield,
    Plus,
    X,
    Save,
    ArrowLeft,
    ArrowUp,
    ArrowDown,
    Hash,
    RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const CrewManagement = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [crewMembers, setCrewMembers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [usersLoading, setUsersLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTab, setSelectedTab] = useState('crew'); // 'crew' or 'users'
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({ crewRole: '', crewBio: '', displayOrder: '' });
    const [submitting, setSubmitting] = useState(false);
    const [syncingFollows, setSyncingFollows] = useState(false);

    useEffect(() => {
        fetchCrewMembers();
        if (selectedTab === 'users') {
            fetchAllUsers();
        }
    }, [selectedTab]);

    const fetchCrewMembers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/crew?limit=50&order_by=display_order&order_dir=ASC');
            if (response.data.success) {
                setCrewMembers(response.data.data);
            } else {
                setError('Failed to load crew members');
            }
        } catch (err) {
            console.error('Error fetching crew members:', err);
            setError('Failed to load crew members');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllUsers = async () => {
        try {
            setUsersLoading(true);
            const response = await api.get(`/admin/crew/users?limit=50&search=${searchTerm}`);
            if (response.data.success) {
                setAllUsers(response.data.data);
            } else {
                setError('Failed to load users');
            }
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load users');
        } finally {
            setUsersLoading(false);
        }
    };

    const handleSearch = () => {
        if (selectedTab === 'users') {
            fetchAllUsers();
        }
    };

    const handleAddToCrew = async (userId) => {
        if (!formData.crewRole.trim()) {
            alert('Please enter a crew role');
            return;
        }

        try {
            setSubmitting(true);
            const response = await api.post(`/admin/crew/${userId}`, {
                crewRole: formData.crewRole.trim(),
                crewBio: formData.crewBio.trim() || null,
                displayOrder: parseInt(formData.displayOrder) || null
            });
            
            if (response.data.success) {
                setShowAddModal(false);
                setSelectedUser(null);
                setFormData({ crewRole: '', crewBio: '', displayOrder: '' });
                await fetchCrewMembers();
                if (selectedTab === 'users') {
                    await fetchAllUsers();
                }
                alert('User added to crew successfully!');
            } else {
                alert(response.data.message || 'Failed to add user to crew');
            }
        } catch (err) {
            console.error('Error adding user to crew:', err);
            alert('Failed to add user to crew');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateCrewMember = async () => {
        if (!formData.crewRole.trim()) {
            alert('Please enter a crew role');
            return;
        }

        try {
            setSubmitting(true);
            const response = await api.put(`/admin/crew/${editingMember.user_id}`, {
                crewRole: formData.crewRole.trim(),
                crewBio: formData.crewBio.trim() || null,
                displayOrder: parseInt(formData.displayOrder) || null
            });
            
            if (response.data.success) {
                setShowEditModal(false);
                setEditingMember(null);
                setFormData({ crewRole: '', crewBio: '', displayOrder: '' });
                await fetchCrewMembers();
                alert('Crew member updated successfully!');
            } else {
                alert(response.data.message || 'Failed to update crew member');
            }
        } catch (err) {
            console.error('Error updating crew member:', err);
            alert('Failed to update crew member');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveFromCrew = async (userId, username) => {
        if (!confirm(`Are you sure you want to remove ${username} from the crew?`)) {
            return;
        }

        try {
            const response = await api.delete(`/admin/crew/${userId}`);
            
            if (response.data.success) {
                await fetchCrewMembers();
                alert('User removed from crew successfully!');
            } else {
                alert(response.data.message || 'Failed to remove user from crew');
            }
        } catch (err) {
            console.error('Error removing user from crew:', err);
            alert('Failed to remove user from crew');
        }
    };

    const openAddModal = (userToAdd) => {
        setSelectedUser(userToAdd);
        // Calculate next display order
        const nextOrder = crewMembers.length > 0 ? Math.max(...crewMembers.map(m => m.display_order || 0)) + 1 : 1;
        setFormData({ crewRole: '', crewBio: '', displayOrder: nextOrder.toString() });
        setShowAddModal(true);
    };

    const openEditModal = (member) => {
        setEditingMember(member);
        setFormData({ 
            crewRole: member.crew_role || '', 
            crewBio: member.crew_bio || '',
            displayOrder: (member.display_order || 0).toString()
        });
        setShowEditModal(true);
    };

    const handleMoveCrewMember = async (userId, direction) => {
        try {
            const response = await api.post(`/admin/crew/${userId}/move`, {
                direction: direction
            });
            
            if (response.data.success) {
                await fetchCrewMembers();
            } else {
                // Don't show alert for "already at top/bottom" messages
                if (response.data.message.includes('already at')) {
                    console.log(response.data.message); // Just log it
                } else {
                    alert(response.data.message || 'Failed to move crew member');
                }
            }
        } catch (err) {
            // Handle 400 errors gracefully (already at top/bottom)
            if (err.response && err.response.status === 400) {
                const message = err.response.data?.message || '';
                if (message.includes('already at')) {
                    console.log(message); // Just log boundary messages
                } else {
                    alert(message || 'Cannot move crew member');
                }
            } else {
                console.error('Error moving crew member:', err);
                alert('Failed to move crew member');
            }
        }
    };

    const handleSyncFollows = async () => {
        if (!confirm('This will remove and re-create follow relationships between all crew members so everyone follows everyone else. Continue?')) {
            return;
        }

        try {
            setSyncingFollows(true);
            const response = await api.post('/admin/crew/sync-follows');

            if (response.data.success) {
                alert(response.data.message || 'Crew members are now all following each other!');
            } else {
                alert(response.data.message || 'Failed to sync crew follows');
            }
        } catch (err) {
            console.error('Error syncing crew follows:', err);
            alert('Failed to sync crew follows');
        } finally {
            setSyncingFollows(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short', 
            day: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/admin')}
                                className="p-2 text-gray-400 hover:text-white transition-colors"
                            >
                                <ArrowLeft className="w-6 h-6" />
                            </button>
                            <div>
                                <h1 className="text-3xl font-black gradient-text flex items-center gap-3">
                                    <Users className="w-8 h-8 text-blue-400" />
                                    Deep Wave Crew Management
                                </h1>
                                <p className="text-gray-400">Manage crew members and user privileges</p>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-400">
                                {crewMembers.length} crew members
                            </span>
                            <div className="text-xs text-gray-500">
                                Sorted by display order
                            </div>
                            <button
                                onClick={handleSyncFollows}
                                disabled={syncingFollows || crewMembers.length < 2}
                                title="Removes and re-creates follow relationships so all crew members follow each other"
                                className="btn-secondary px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
                            >
                                <RefreshCw className={`w-4 h-4 ${syncingFollows ? 'animate-spin' : ''}`} />
                                Sync Crew Follows
                            </button>
                        </div>
                    </div>

                    {/* Tab Navigation */}
                    <div className="glass-card p-1 inline-flex rounded-lg">
                        <button
                            onClick={() => setSelectedTab('crew')}
                            className={`px-4 py-2 rounded-md transition-all flex items-center gap-2 ${
                                selectedTab === 'crew'
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-400 hover:text-white'
                            }`}
                        >
                            <Users className="w-4 h-4" />
                            Current Crew
                        </button>
                        <button
                            onClick={() => setSelectedTab('users')}
                            className={`px-4 py-2 rounded-md transition-all flex items-center gap-2 ${
                                selectedTab === 'users'
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-400 hover:text-white'
                            }`}
                        >
                            <UserPlus className="w-4 h-4" />
                            Add Members
                        </button>
                    </div>
                </div>

                {/* Search Bar for Users Tab */}
                {selectedTab === 'users' && (
                    <div className="mb-6">
                        <div className="glass-card p-4">
                            <div className="flex items-center gap-4">
                                <div className="flex-1 relative">
                                    <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search users by username, name, or email..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                    />
                                </div>
                                <button
                                    onClick={handleSearch}
                                    className="btn-primary px-6"
                                >
                                    Search
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Content */}
                {selectedTab === 'crew' ? (
                    // Current Crew Members
                    <div className="glass-card">
                        <div className="p-6 border-b border-gray-700">
                            <h2 className="text-xl font-bold text-white">The Deep Wave Crew Members</h2>
                            <p className="text-gray-400">Manage existing crew members</p>
                        </div>

                        {loading ? (
                            <div className="p-8 text-center">
                                <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                                <p className="text-gray-400">Loading crew members...</p>
                            </div>
                        ) : error ? (
                            <div className="p-8 text-center">
                                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                                <p className="text-red-400 mb-4">{error}</p>
                                <button onClick={fetchCrewMembers} className="btn-primary">
                                    Retry
                                </button>
                            </div>
                        ) : crewMembers.length === 0 ? (
                            <div className="p-8 text-center">
                                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-400 mb-4">No crew members yet</p>
                                <p className="text-gray-500 text-sm">Switch to "Add Members" to start building your crew</p>
                            </div>
                        ) : (
                            <div className="p-6">
                                <div className="grid gap-4">
                                    {crewMembers.map((member) => (
                                        <div key={member.id} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                                                        {member.profile_picture_url ? (
                                                            <img
                                                                src={member.profile_picture_url}
                                                                alt="Profile"
                                                                width="48"
                                                                height="48"
                                                                className="w-12 h-12 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <span className="text-2xl leading-none">🌊</span>
                                                        )}
                                                    </div>
                                                    
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <h3 className="font-semibold text-white">
                                                                {member.display_name || member.username}
                                                            </h3>
                                                            {member.user_id === user?.id && (
                                                                <Crown className="w-4 h-4 text-yellow-400" />
                                                            )}
                                                            {member.is_verified && (
                                                                <CheckCircle className="w-4 h-4 text-blue-400" />
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-blue-400 font-medium">
                                                            {member.crew_role}
                                                        </p>
                                                        <p className="text-xs text-gray-400">
                                                            @{member.username} • {member.email}
                                                        </p>
                                                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                                            <Calendar className="w-3 h-3" />
                                                            Joined crew {formatDate(member.crew_joined_date)}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-1">
                                                    <div className="flex flex-col">
                                                        <button
                                                            onClick={() => handleMoveCrewMember(member.user_id, 'up')}
                                                            className="p-1 text-gray-400 hover:text-blue-400 transition-colors"
                                                            title="Move up"
                                                        >
                                                            <ArrowUp className="w-3 h-3" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleMoveCrewMember(member.user_id, 'down')}
                                                            className="p-1 text-gray-400 hover:text-blue-400 transition-colors"
                                                            title="Move down"
                                                        >
                                                            <ArrowDown className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                    <div className="text-xs text-gray-500 mx-1 min-w-6 text-center">
                                                        #{member.display_order || 0}
                                                    </div>
                                                    <button
                                                        onClick={() => openEditModal(member)}
                                                        className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                                                        title="Edit crew member"
                                                    >
                                                        <Edit3 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleRemoveFromCrew(member.user_id, member.username)}
                                                        className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                                                        title="Remove from crew"
                                                    >
                                                        <UserMinus className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            {member.crew_bio && (
                                                <div className="mt-3 pt-3 border-t border-gray-700/30">
                                                    <p className="text-sm text-gray-400">{member.crew_bio}</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    // All Users for Adding to Crew
                    <div className="glass-card">
                        <div className="p-6 border-b border-gray-700">
                            <h2 className="text-xl font-bold text-white">Add Users to Crew</h2>
                            <p className="text-gray-400">Select users to add to the Deep Wave crew</p>
                        </div>

                        {usersLoading ? (
                            <div className="p-8 text-center">
                                <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                                <p className="text-gray-400">Loading users...</p>
                            </div>
                        ) : allUsers.length === 0 ? (
                            <div className="p-8 text-center">
                                <span className="text-5xl block mb-4">🌊</span>
                                <p className="text-gray-400 mb-4">No users found</p>
                                <p className="text-gray-500 text-sm">Try adjusting your search terms</p>
                            </div>
                        ) : (
                            <div className="p-6">
                                <div className="grid gap-4">
                                    {allUsers.map((user) => (
                                        <div key={user.id} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-700 rounded-full flex items-center justify-center">
                                                        {user.profile_picture_url ? (
                                                            <img
                                                                src={user.profile_picture_url}
                                                                alt="Profile"
                                                                width="40"
                                                                height="40"
                                                                className="w-10 h-10 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <span className="text-xl leading-none">🌊</span>
                                                        )}
                                                    </div>
                                                    
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <h3 className="font-medium text-white">
                                                                {user.display_name || user.username}
                                                            </h3>
                                                            {user.is_verified && (
                                                                <CheckCircle className="w-4 h-4 text-blue-400" />
                                                            )}
                                                            {user.is_admin && (
                                                                <Shield className="w-4 h-4 text-yellow-400" />
                                                            )}
                                                            {user.is_crew_member && (
                                                                <Star className="w-4 h-4 text-blue-400" />
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-gray-400">
                                                            @{user.username} • {user.email}
                                                        </p>
                                                        {user.is_crew_member && (
                                                            <p className="text-xs text-blue-400">
                                                                Current role: {user.crew_role}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div>
                                                    {user.is_crew_member ? (
                                                        <span className="px-3 py-1 bg-blue-600/20 text-blue-400 text-sm rounded-full">
                                                            Already Crew
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => openAddModal(user)}
                                                            className="btn-primary px-4 py-2 text-sm flex items-center gap-1"
                                                        >
                                                            <UserPlus className="w-4 h-4" />
                                                            Add to Crew
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Add to Crew Modal */}
                {showAddModal && selectedUser && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="glass-card p-6 max-w-md w-full mx-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-xl font-bold text-white">Add to Crew</h3>
                                <button
                                    onClick={() => setShowAddModal(false)}
                                    aria-label="Close"
                                    className="p-2 text-gray-400 hover:text-white"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="mb-4">
                                <p className="text-gray-400">Adding: <span className="text-white font-medium">{selectedUser.display_name || selectedUser.username}</span></p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Crew Role *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.crewRole}
                                        onChange={(e) => setFormData(prev => ({ ...prev, crewRole: e.target.value }))}
                                        placeholder="e.g., Sound Engineer, Producer, Manager"
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-600"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        <Hash className="w-4 h-4 inline mr-1" />
                                        Display Order
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.displayOrder}
                                        onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: e.target.value }))}
                                        placeholder="1"
                                        min="0"
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-600"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Bio (optional)
                                    </label>
                                    <textarea
                                        value={formData.crewBio}
                                        onChange={(e) => setFormData(prev => ({ ...prev, crewBio: e.target.value }))}
                                        placeholder="Brief description of their role and expertise..."
                                        rows={3}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-600"
                                    />
                                </div>

                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => handleAddToCrew(selectedUser.user_id)}
                                        disabled={submitting || !formData.crewRole.trim()}
                                        className="btn-primary flex-1 flex items-center justify-center gap-2"
                                    >
                                        {submitting ? (
                                            <div className="loading-spinner w-4 h-4" />
                                        ) : (
                                            <Save className="w-4 h-4" />
                                        )}
                                        Add to Crew
                                    </button>
                                    <button
                                        onClick={() => setShowAddModal(false)}
                                        className="btn-ghost px-4"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Crew Member Modal */}
                {showEditModal && editingMember && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="glass-card p-6 max-w-md w-full mx-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-xl font-bold text-white">Edit Crew Member</h3>
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    aria-label="Close"
                                    className="p-2 text-gray-400 hover:text-white"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="mb-4">
                                <p className="text-gray-400">Editing: <span className="text-white font-medium">{editingMember.display_name || editingMember.username}</span></p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Crew Role *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.crewRole}
                                        onChange={(e) => setFormData(prev => ({ ...prev, crewRole: e.target.value }))}
                                        placeholder="e.g., Sound Engineer, Producer, Manager"
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-600"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        <Hash className="w-4 h-4 inline mr-1" />
                                        Display Order
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.displayOrder}
                                        onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: e.target.value }))}
                                        placeholder="1"
                                        min="0"
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-600"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Bio (optional)
                                    </label>
                                    <textarea
                                        value={formData.crewBio}
                                        onChange={(e) => setFormData(prev => ({ ...prev, crewBio: e.target.value }))}
                                        placeholder="Brief description of their role and expertise..."
                                        rows={3}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-600"
                                    />
                                </div>

                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={handleUpdateCrewMember}
                                        disabled={submitting || !formData.crewRole.trim()}
                                        className="btn-primary flex-1 flex items-center justify-center gap-2"
                                    >
                                        {submitting ? (
                                            <div className="loading-spinner w-4 h-4" />
                                        ) : (
                                            <Save className="w-4 h-4" />
                                        )}
                                        Update Member
                                    </button>
                                    <button
                                        onClick={() => setShowEditModal(false)}
                                        className="btn-ghost px-4"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CrewManagement;